import { ExtendedFAB, Icon, styleClassNames, tableClassNames, TableInner, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';

import type { TTableRowSelection as RowSelectionState } from '@chocolate-soup-inc/cs-frontend-components';
import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { ALL_GIFT_TYPES, getReadableGiftType } from '../../entities/gifts/shared';
import { TGiftTypes } from '../../generated/graphql';

import { CountryName } from '../../components/coutryName/CountryName';
import { Filters, TFiltersProps } from '../../components/filters/Filters';
import { FloatingActions } from '../../components/floatingActions/FloatingActions';
import { TablePage, TTablePageRef } from '../../components/tablePage/TablePage';

import { readableDate } from '@chocolate-soup-inc/cs-api-consumer-utils';
import clsx from 'clsx';
import { useFilter } from '../../contexts/filters';
import { isAddressValid } from '../../entities/addresses/shared';
import { TGiftType, useQueryOrphanGifts } from '../../entities/gifts/queries';
import { MovingToDoneModalContent } from './MovingToDoneModalContent';
import { useGiftsFilters } from './shared';

export const OrphanGifts = () => {
  const { filtersPageMode } = useFilter();
  useEffect(() => {
    filtersPageMode();
  }, []); //eslint-disable-line

  const tableRef = createRef<TTablePageRef>();

  const { data: gifts, error, loading } = useQueryOrphanGifts();

  const {
    companies,
    companiesCounts,
    companyFilter,
    filterByCompany,
    setCompany,
    offices,
    officeFilter,
    filterByOffice,
    setOffice,
    months,
    monthFilter,
    filterByMonth,
    setMonth,
    eventTypeFilter,
    setEventTypeFilter,
    filterByEventType,
    recipientNameFilter,
    setRecipientNameFilter,
    filterByRecipientName,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter,
    filterByClientManager
  } = useGiftsFilters({ gifts });

  const [selectedRows, setSelectedRows] = useState<string[]>();
  const [giftsGoingToDone, setGiftsGoingToDone] = useState<TGiftType[]>();
  //const [includingNextDays, setIncludingNextDays] = useState<number>(60);

  const filteredEvents = useMemo(() => {
    return gifts.filter((e) => {
      return filterByCompany(e) && filterByOffice(e) && filterByMonth(e) && filterByEventType(e) && filterByClientManager(e) && filterByRecipientName(e);
    });
  }, [filterByCompany, filterByMonth, filterByOffice, gifts, filterByEventType, filterByClientManager, filterByRecipientName]);

  //console.log('gifts NEW?:', gifts);
  //console.log('filteredGifts NEW?:', filteredEvents);

  const onRowSelectionChange = useCallback((rowSelection: RowSelectionState) => {
    console.log('selectedRows?:', rowSelection);
    setSelectedRows(Object.keys(rowSelection));
  }, []);

  const getRowId = useCallback((event: TGiftType) => {
    return event.id;
  }, []);

  const getRowClassName = useCallback((row: Record<string, any>) => {
    if (!row.original.address || !row.original.recipient) return styleClassNames.attentionRow;
  }, []);

  const filters = useMemo(() => {
    const filtersList: TFiltersProps['filters'] = [];

    filtersList.push({
      type: 'singleSelect',
      label: 'Date',
      name: 'date',
      onChange: setMonth,
      options: months.map((m) => ({ label: m, value: m })),
      value: monthFilter
    });

    filtersList.push({
      disabled: companies.length === 0,
      type: 'singleSelect',
      label: 'Company',
      name: 'companyId',
      onChange: setCompany,
      value: companyFilter,
      options: companies?.map((c) => ({
        label: `${c.name} (${companiesCounts[c.id]})`,
        value: c.id
      }))
    });

    if (companyFilter && offices.length > 0)
      filtersList.push({
        type: 'singleSelect',
        label: 'Office',
        name: 'office',
        onChange: setOffice,
        options: offices.map((o) => ({ label: o.name, value: o.id })),
        value: officeFilter
      });

    filtersList.push({
      type: 'textInput',
      label: 'Recipient Name',
      name: 'recipientName',
      onChange: (v) => {
        setRecipientNameFilter(v != null ? (v as string) : undefined);
      },
      value: recipientNameFilter
    });

    filtersList.push({
      disabled: clientManagers.length === 0,
      label: 'Client Manager',
      name: 'clientManager',
      onChange: (v) => {
        setClientManagerFilter(v != null ? (v as string) : undefined);
      },
      options: clientManagers.map((c) => ({ label: c.name, value: c.id })),
      type: 'singleSelect',
      value: clientManagerFilter
    });

    filtersList.push({
      type: 'singleSelect',
      label: 'Event Type',
      name: 'eventType',
      onChange: (v) => {
        setEventTypeFilter(v != null ? (v as TGiftTypes) : undefined);
      },
      options: Object.entries(ALL_GIFT_TYPES).map(([key, value]) => ({ label: value, value: key })),
      value: eventTypeFilter
    });

    /*filtersList.push({
      includeEmptyOption: false,
      type: 'singleSelect',
      label: 'Including Next Days',
      name: 'includingNextDays',
      onChange: (v) => {
        setIncludingNextDays(v != null ? Number(v) : 60);
      },
      options: [
        {
          label: '60 Days',
          value: 60,
        },
        {
          label: '90 Days',
          value: 90,
        },
        {
          label: 'All Days',
          value: 999,
        },
      ],
      value: includingNextDays,
    });*/

    return filtersList;
  }, [
    companies,
    companiesCounts,
    companyFilter,
    monthFilter,
    months,
    officeFilter,
    offices,
    setCompany,
    setMonth,
    setOffice,
    eventTypeFilter,
    setEventTypeFilter,
    recipientNameFilter,
    setRecipientNameFilter,
    clientManagers,
    clientManagerFilter,
    setClientManagerFilter
  ]);

  const onMoveToDoneCancel = useCallback(() => {
    setGiftsGoingToDone(undefined);
  }, []);

  const onMoveToDoneConfirm = useCallback(() => {
    setGiftsGoingToDone(undefined);
  }, []);

  const onOrphanGiftsClick = useCallback(() => {
    //if (!selectedGifts.every((g) => g.status == TGiftStatuses.Done && g.isDone == 1)) return;
    const giftsToDone = (selectedRows?.map((id) => gifts.find((g) => g.id == id)) || []) as TGiftType[];
    console.log(giftsToDone);
    setGiftsGoingToDone(giftsToDone);
    setSelectedRows([]);
  }, [selectedRows, gifts]);

  return (
    <TablePage dataLoading={loading} error={error} ref={tableRef} title='Orphan Gifts'>
      <Filters filters={filters} />
      <div style={{ overflow: 'auto' }}>
        <TableInner<TGiftType>
          enableSorting={true}
          defaultSorting={{ id: 'shippingDate', desc: false }}
          data={filteredEvents}
          emptyText={gifts.length === 0 ? 'No upcoming one-off gifts.' : 'No upcoming one-off gifts for the current filters.'}
          expandable={true}
          fillSelectedRows={true}
          fixedHeader={true}
          forceExpanded={true}
          getRowId={getRowId}
          getRowClassName={getRowClassName}
          hideSubRowsSelect={true}
          hoverableRows={true}
          onRowSelectionChange={onRowSelectionChange}
          selectableRows={true}
          selectingChildSelectsFather={true}
          virtual={false}
          columns={[
            {
              header: 'Company Name',
              accessorKey: 'company.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.employee?.isPaused && (
                      <Tooltip message='This gift was paused.'>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.company?.name}</span>
                  </div>
                );
              }
            },
            {
              header: 'Recipient Name',
              accessorKey: 'recipient.fullName',
              cell: ({ cell }) => {
                let text = '';
                const pausePeriod = cell.row.original.employee?.pausePeriod;
                if (pausePeriod) {
                  const { fromDate, toDate } = pausePeriod;
                  if (fromDate) text += ` since ${readableDate(fromDate)}`;
                  if (toDate) text += ` to ${readableDate(toDate)}`;
                }
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                    {cell.row.original.employee?.isPaused && (
                      <Tooltip message={`Employee paused${text}.`}>
                        <Icon icon='pause_circle' />
                      </Tooltip>
                    )}
                    <span>{cell.row.original.recipient?.fullName}</span>
                  </div>
                );
              }
            },
            {
              header: 'Shipping Date',
              accessorKey: 'shippingDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(styleClassNames.inlineLabel, tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.shippingDate ?? undefined)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Date',
              accessorKey: 'eventDate',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{readableDate(cell.row.original.eventDate)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Event Type',
              accessorKey: 'type',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.tertiary)}>
                    <span>{cell.row.original.type === TGiftTypes.Other ? cell.row.original.customType : getReadableGiftType(cell.row.original)}</span>
                  </div>
                );
              }
            },
            {
              header: 'Office',
              accessorKey: 'employee.office.name',
              cell: ({ cell }) => {
                return (
                  <div className={clsx(tableClassNames.secondary)}>
                    <span>{cell.row.original.employee?.office?.name || '-'}</span>
                  </div>
                );
              }
            },
            {
              header: 'Country',
              accessorKey: 'address.country',
              cell: ({ cell }) => {
                if (isAddressValid(cell.row.original.address))
                  return (
                    <div className={clsx(tableClassNames.primary)}>
                      <CountryName name={cell.row.original.address?.country || undefined} />
                    </div>
                  );
                else
                  return (
                    <div className={clsx(styleClassNames.inlineLabel, tableClassNames.primary)}>
                      <Icon icon='warning' />
                      <span> data</span>
                    </div>
                  );
              }
            }
          ]}
        />
      </div>
      {giftsGoingToDone && giftsGoingToDone.length > 0 && (
        <MovingToDoneModalContent
          //allGifts={gifts || []}
          gifts={giftsGoingToDone}
          onCancel={onMoveToDoneCancel}
          onSuccess={onMoveToDoneConfirm}
        />
      )}
      {selectedRows && selectedRows.length > 0 && (
        <FloatingActions>
          <ExtendedFAB label={`Manually ship ${selectedRows.length} gift${selectedRows.length > 1 ? 's' : ''}`} leadingIcon='local_shipping' onClick={onOrphanGiftsClick} variant='secondary' />
        </FloatingActions>
      )}
    </TablePage>
  );
};
